/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadVayuQuickProReport } from "../../../../utils/downloadUtils";

const VayuQuickPro = () => {
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [headerHubList, setHeaderHubList] = useState([]);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for VayuQuickPro Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedBookingHubs, setSelectedBookingHubs] = useState([]);
    const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                // setLoading(false);
            }
        })();
    }, []);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedBookingHubs([newActiveHubId]);
            } else {
                setSelectedBookingHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedBookingHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedBookingHubs([]);
            } else {
                setSelectedBookingHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    const calendarRef = useRef();

    // Custom handler for date range change
    const handleDateRangeChange = (value) => {
        if (!value || !value[0] || !value[1]) {
            // If cleared, reset to today and open calendar
            setDateRange([moment(), moment()]);
            // Open the calendar dropdown
            if (calendarRef.current && calendarRef.current.picker) {
                calendarRef.current.picker.open();
            }
        } else {
            setDateRange(value);
        }
    };

    // Fetch report data when filters or pagination change
    useEffect(() => {
        if (!hubUser) return;
        if (hubUser === "ROLE_HUBADMIN" && selectedBookingHubs.length === 0) return;
        const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
        if (!hasValidDates) return;
        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedBookingHubs, selectedDestinationHubs, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";
            if (!hasValidDates) {
                setLoading(false);
                return;
            }
            setReportData([]); // This is fine here, because the guard above prevents it from running on clear

            // Resolve Booking Hubs (selectedBookingHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedBookingHubIds = selectedBookingHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedBookingHubs;

            const selectedBookingHubData = activeHubOptions.filter(hub => resolvedBookingHubIds?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));

            const params = {
                ...(resolvedBookingHubIds?.length > 0 && { bookingHubIds: resolvedBookingHubIds.join(",") }),
                ...(selectedDestinationHubs?.length > 0 && { destinationHubIds: selectedDestinationHubs.join(",") }),
                ...(hasValidDates && {
                    startDate: dateRange[0].format("YYYY-MM-DD"),
                    endDate: dateRange[1].format("YYYY-MM-DD")
                }),
                pageNum: pagination.current,
                pageSize: pagination.pageSize
            };

            // Call the VayuQuickPro report API
            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getVayuQuickProReport(params);
            setReportData(data);
            setLastsynced(lastRefreshedAt);
            setNextScheduledRefresh(nextScheduledRefresh);
            setPagination(prev => ({
                ...prev,
                total,
            }));
            setLoading(false);
        } catch (error) {
            message.error("Failed to fetch report data");
            setReportData([]);
            setPagination(prev => ({
                ...prev,
                total: 0
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }

        /*if (selectedBookingHubs.length === 0) {
             message.error("Please select at least one booking hub for download");
             return;
         }*/

        setDownloadLoading(true);
        try {
            // Resolve Booking Hubs (selectedBookingHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedBookingHubIds = selectedBookingHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedBookingHubs;

            const selectedBookingHubData = activeHubOptions.filter(hub => resolvedBookingHubIds?.includes(hub.value));
            const selectedDestHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));

            const downloadParams = {
                ...(resolvedBookingHubIds?.length > 0 && { bookingHubIds: resolvedBookingHubIds.join(",") }),
                ...(selectedDestinationHubs?.length > 0 && { destinationHubIds: selectedDestinationHubs.join(",") }),
                startDate: dateRange[0].format("YYYY-MM-DD"),
                endDate: dateRange[1].format("YYYY-MM-DD")
            };

            await downloadVayuQuickProReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    // Table columns based on the API response structure
    const columns = [
        {
            title: "No.",
            dataIndex: "index",
            key: "index",
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "Document No",
            dataIndex: "documentNo",
            key: "documentNo",
            sorter: true,
            width: 140,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            sorter: true,
            width: 120
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            key: "serviceType",
            sorter: true,
            width: 140
        },

        {
            title: "Booking Date",
            dataIndex: "bookingDate",
            key: "bookingDate",
            sorter: true,
            width: 120
        },
        {
            title: "Booking CP",
            dataIndex: "bookingCp",
            key: "bookingCp",
            sorter: true,
            width: 150
        },
        {
            title: "CP Type",
            dataIndex: "cpType",
            key: "cpType",
            sorter: true,
            width: 100
        },
        {
            title: "Booking Hub",
            dataIndex: "bookingHub",
            key: "bookingHub",
            sorter: true,
            width: 180
        },
        {
            title: "Destination Hub",
            dataIndex: "destinationHub",
            key: "destinationHub",
            sorter: true,
            width: 180
        },
        {
            title: "Dox/Non-Dox",
            dataIndex: "doxNondox",
            key: "doxNondox",
            sorter: true,
            width: 100
        },
        {
            title: "Zone",
            dataIndex: "zone",
            key: "zone",
            sorter: true,
            width: 100
        },
        {
            title: "Travel By",
            dataIndex: "travelBy",
            key: "travelBy",
            sorter: true,
            width: 100
        },
        {
            title: "Weight (gms)",
            dataIndex: "wtInGms",
            key: "wtInGms",
            sorter: true,
            width: 120
        },
        {
            title: "Amount Billed",
            dataIndex: "amountBilledToCustomer",
            key: "amountBilledToCustomer",
            sorter: true,
            width: 120,
            render: (text) => `₹${text || 0}`
        },
        {
            title: "Freight Charges",
            dataIndex: "freightCharges",
            key: "freightCharges",
            sorter: true,
            width: 120,
            render: (text) => `₹${text || 0}`
        },
        {
            title: "Sender",
            dataIndex: "sender",
            key: "sender",
            sorter: true,
            width: 180
        },
        {
            title: "Receiver",
            dataIndex: "receiver",
            key: "receiver",
            sorter: true,
            width: 180
        },
        {
            title: "From City",
            dataIndex: "fromCity",
            key: "fromCity",
            sorter: true,
            width: 120
        },
        {
            title: "To City",
            dataIndex: "toCity",
            key: "toCity",
            sorter: true,
            width: 120
        },
        {
            title: "To State",
            dataIndex: "toState",
            key: "toState",
            sorter: true,
            width: 120
        },
        {
            title: "From State",
            dataIndex: "fromState",
            key: "fromState",
            sorter: true,
            width: 120
        },
        {
            title: "To Pincode",
            dataIndex: "toPincode",
            key: "toPincode",
            sorter: true,
            width: 120
        },
        {
            title: "Current Premise Name",
            dataIndex: "currentPremiseName",
            key: "currentPremiseName",
            sorter: true,
            width: 170
        },
        {
            title: "Current Status Time",
            dataIndex: "currentStatusTime",
            key: "currentStatusTime",
            sorter: true,
            width: 150
        },
        {
            title: "Current Status",
            dataIndex: "auditStatus",
            key: "auditStatus",
            sorter: true,
            width: 120
        },
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="VayuQuickPro Report" />
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Date</div>
                        <CalendarComponent
                            ref={calendarRef}
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Hub</div>
                        <DropDownComponent
                            placeholder="Select Booking Hubs"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedBookingHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedBookingHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Destination Hub</div>
                        <DropDownComponent
                            placeholder="Select Destination Hubs"
                            options={hubList}
                            onChange={(val) => {
                                setSelectedDestinationHubs(val);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                </Row>
                <Row style={{ marginTop: 16 }}>
                    <Col span={24} style={{ textAlign: "right" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange || !Array.isArray(dateRange) || !dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
                rowKey="documentNo"
            />
        </div>
    );
};

export default VayuQuickPro; 
